// Shared helpers for persisting a manually-edited "order date" field
// (order_placed / order_date) in sessionStorage across navigation, scoped to
// one exact client + case + template combination.
//
// Key format: date-edit:{user_id}:{team_id}:{client_id}:{order_id}:{template_id}
//
// client_id is required: without it, two different clients that both lack an
// order_id would collapse into the same "none" key and leak each other's
// edited date.

export const DATE_EDIT_KEY_PREFIX = "date-edit:";

export function buildDateEditKey({ userId, teamId, clientId, orderId, templateId }) {
  const uid = userId || "";
  const tid = teamId || "default";
  const cid = clientId || "none";
  const oid = orderId || "none";
  const tpl = templateId || "";
  return `${DATE_EDIT_KEY_PREFIX}${uid}:${tid}:${cid}:${oid}:${tpl}`;
}

// Encapsulates what to do with the persisted date-edit sessionStorage key
// once a send attempt resolves. A successful send ("sent") drops the entry;
// anything else (mock/failed) leaves it so the operator can recover it on
// retry. Used by EnviarPage.js so tests exercise the real logic instead of
// re-implementing the condition.
export function handleSendResultForDateEdit(status, key, storage = typeof sessionStorage !== "undefined" ? sessionStorage : null) {
  if (status !== "sent") return;
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    /* unavailable */
  }
}

// Returns "today" as MM/DD/YYYY in Dominican Republic time (UTC-4, no DST),
// independent of the browser/runtime's own timezone. Used to auto-prefill
// order_placed / order_date fields in EnviarPage.
export function getTodayInRD(nowMs = Date.now()) {
  const drNow = new Date(nowMs - 4 * 60 * 60 * 1000);
  const mm = String(drNow.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(drNow.getUTCDate()).padStart(2, "0");
  const yyyy = drNow.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// Used on logout to drop every persisted date-edit entry without touching
// unrelated sessionStorage keys (e.g. "license_expired_msg"). Our composite
// key always starts with the fixed DATE_EDIT_KEY_PREFIX.
export function clearAllDateEditKeys(storage = typeof sessionStorage !== "undefined" ? sessionStorage : null) {
  if (!storage) return;
  Object.keys(storage).forEach((k) => {
    if (k.startsWith(DATE_EDIT_KEY_PREFIX)) storage.removeItem(k);
  });
}
